import { Box } from '@mui/material'
import { DataGridPremiumProps, GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid-premium'
import { MouseEvent, ReactNode, useCallback, useMemo } from 'react'
import { DataGridBase, DataGridBaseProps, TableEditMode } from '../data-grid/DataGridBase'
import { ControlledTableFilterPanel } from './components/ControlledTableFilterPanel'
import { ControlledTableToolbar, ExportFormat, ExportOptions } from './components/ControlledTableToolbar'

type GridProps = Pick<
  DataGridBaseProps,
  | 'apiRef'
  | 'pinnedColumns'
  | 'rows'
  | 'columns'
  | 'loading'
  | 'onRowClick'
  | 'isRowClickable'
  | 'getDefaultRowLink'
  | 'processRowUpdate'
  | 'paginationMode'
  | 'sortingMode'
  | 'filterMode'
  | 'TableEmptyState'
  | 'onPinnedColumnsChange'
  | 'groupingColDef'
  | 'unstable_dataSource'
  | 'dataSourceResyncQueryKey'
  | 'resyncDataSource'
  | 'enableServerSideRowGrouping'
  | 'sanitizeRestoredGrouping'
  | 'unpinColumnsWhileGrouped'
  | 'rowGroupingModel'
  | 'onIsGroupedChange'
  | 'isGroupExpandedByDefault'
  | 'checkboxSelection'
  | 'rowSelectionModel'
  | 'onRowSelectionModelChange'
  | 'getRowSelectionStatus'
  | 'checkboxSelectionVisibleOnly'
  | 'keepNonExistentRowsSelected'
  | 'getRowHeight'
  | 'isCellEditable'
  | 'onCellEditStart'
  | 'disableColumnReorder'
  | 'defaultColumnVisibilityModel'
>

type ControlledGridProps = Required<
  Pick<
    DataGridPremiumProps,
    | 'filterModel'
    | 'onFilterModelChange'
    | 'sortModel'
    | 'onSortModelChange'
    | 'paginationModel'
    | 'onPaginationModelChange'
    | 'columnVisibilityModel'
    | 'onColumnVisibilityModelChange'
  >
>

export interface ControlledTableProps extends GridProps, ControlledGridProps {
  tableName: string
  enableAiFilter?: boolean
  exportOptions?: ExportOptions
  totalCount?: number
  totalCountLoading?: boolean
  totalCountIsApprox?: boolean
  hideRowGrouping?: boolean
  hideSaveFilterButton?: boolean
  hideFooter?: boolean
  customFooter?: ReactNode
  hideSearchBar?: boolean
  hideFilterPanel?: boolean
  disableExportInToolbar?: boolean
  exportLoading?: boolean
  onExportClick?: (event: MouseEvent<HTMLElement>) => void
  onExportFormat?: (format: ExportFormat) => void
  toolbarHeader?: ReactNode
  toolbarActions?: ReactNode
  editMode?: TableEditMode
  pagination?: boolean
  disableColumnFilter?: boolean
  disableColumnSelector?: boolean
  allowPageScroll?: boolean
  fillHeight?: boolean
  initialState?: {
    pinnedColumns?: GridPinnedColumnFields
    rowGrouping?: {
      model: string[]
    }
  }
  pinnedColumns?: GridPinnedColumnFields
  onPinnedColumnsChange?: (newPinnedColumns: GridPinnedColumnFields) => void
  disableColumnMenu?: boolean
  groupingColDef?: {
    leafField?: string
    headerName?: string
    width?: number
    minWidth?: number
    flex?: number
    colSpan?: GridColDef['colSpan']
    // Hide MUI's "(N)" on group rows: with a data source it counts loaded children, not the true total.
    hideDescendantCount?: boolean
  }
  pageSizeOptions?: number[]
  hasExternalHeader?: boolean
}

export const ControlledTable: React.FC<ControlledTableProps> = ({
  tableName,
  enableAiFilter,
  exportOptions,
  totalCount,
  totalCountLoading = false,
  totalCountIsApprox = false,
  hideRowGrouping = false,
  hideSaveFilterButton = false,
  hideFooter = false,
  customFooter,
  hideSearchBar = false,
  hideFilterPanel = false,
  disableExportInToolbar = false,
  exportLoading = false,
  onExportClick,
  onExportFormat,
  pagination = true,
  allowPageScroll = false,
  fillHeight = false,
  toolbarHeader,
  toolbarActions,
  editMode,
  initialState,
  pinnedColumns,
  onPinnedColumnsChange,
  pageSizeOptions,
  hasExternalHeader = false,
  ...gridProps
}) => {
  // These slot references must stay stable across renders. Re-renders triggered by
  // background refetches (e.g. server-side pagination toggling `isFetching`) would
  // otherwise rebuild the slot functions, remounting and closing the open filter panel.
  const filterPanelSlot = useCallback(
    () => <ControlledTableFilterPanel hideSaveButton={hideSaveFilterButton} />,
    [hideSaveFilterButton]
  )
  const footerSlot = useCallback(() => customFooter, [customFooter])
  const slots = useMemo(
    () => ({
      toolbar: ControlledTableToolbar,
      ...(hideFilterPanel ? {} : { filterPanel: filterPanelSlot }),
      ...(customFooter != null && { footer: footerSlot }),
    }),
    [hideFilterPanel, filterPanelSlot, customFooter, footerSlot]
  )

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
        ...(fillHeight && { height: '100%' }),
        ...(customFooter != null && {
          '& .MuiDataGrid-footerContainer': {
            maxHeight: 'none',
            background: 'transparent',
            boxShadow: 'none',
          },
        }),
      }}
      data-testid="controlled-table"
    >
      <DataGridBase
        stateKey={tableName}
        apiRef={gridProps.apiRef}
        allowPageScroll={allowPageScroll}
        {...gridProps}
        hideFooter={customFooter != null ? false : hideFooter}
        pagination={pagination}
        editMode={editMode}
        rowCount={totalCount}
        totalCountLoading={totalCountLoading}
        totalCountIsApprox={totalCountIsApprox}
        initialState={initialState}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={onPinnedColumnsChange}
        hasExternalHeader={hasExternalHeader}
        slots={slots}
        slotProps={{
          toolbar: {
            tableName,
            enableAiFilter,
            exportOptions,
            displayRowGroupingButton: !hideRowGrouping,
            hideSearchBar,
            disableExportInToolbar,
            exportLoading,
            onExportClick,
            onExportFormat,
            header: toolbarHeader,
            actions: toolbarActions,
            hideControlsRow:
              hideSearchBar && disableExportInToolbar && gridProps.disableColumnFilter && gridProps.disableColumnSelector,
          },
          columnsManagement: {
            searchInputProps: {
              variant: 'standard',
            },
          },
        }}
        unstable_dataSource={gridProps.unstable_dataSource}
        pageSizeOptions={pageSizeOptions}
      />
    </Box>
  )
}
